/**
 * VueGoodTable Pagination Mixin
 *
 * Bu mixin, vue-good-table kullanan tüm sayfalarda
 * sayfa numarası input'u ile pagination özelliğini sağlar.
 *
 * KULLANIM ÖRNEĞİ:
 *
 * <template>
 *   <vue-good-table
 *     mode="remote"
 *     :total-rows="totalRecords"
 *     :is-loading.sync="isLoading"
 *     :show-footer="true"
 *     :pagination-options="getPaginationOptions(filter.limit, filter.page)"
 *     :rows="rows"
 *     :columns="getColumns"
 *     @on-page-change="onPageChange"
 *     @on-per-page-change="onPerPageChange"
 *   >
 *   </vue-good-table>
 * </template>
 *
 * <script>
 * import { vueGoodTablePagination } from '@core/mixins/vueGoodTablePagination';
 *
 * export default {
 *   mixins: [vueGoodTablePagination],
 *   data() {
 *     return {
 *       filter: {
 *         page: 1,
 *         limit: 10
 *       },
 *       rows: [],
 *       totalRecords: 0
 *     };
 *   },
 *   methods: {
 *     onPageChange(params) {
 *       this.filter.page = params.currentPage;
 *       this.loadItems();
 *     },
 *     onPerPageChange(params) {
 *       this.filter.page = 1;
 *       this.filter.limit = params.currentPerPage;
 *       this.loadItems();
 *     }
 *   }
 * };
 * </script>
 *
 * BASİT KULLANIM (perPage ve currentPage sabitse):
 * :pagination-options="paginationOptions"
 *
 * DİNAMİK KULLANIM (perPage ve currentPage değişkense):
 * :pagination-options="getPaginationOptions(filter.limit, filter.page)"
 *
 * NOT: Vue-good-table component'i :pagination-options prop'unu ZORUNLU olarak bekler.
 * Mixin sadece bu prop için hazır değerler sağlar, template'te kullanmak gerekir.
 */
import { useUtils as useI18nUtils } from '@core/libs/i18n';

export const vueGoodTablePagination = {
  setup() {
    const { t } = useI18nUtils();
    return {
      t
    };
  },
  computed: {
    /**
     * VueGoodTable için pagination options (basit kullanım)
     * mode: 'pages' ile sayfa numarası input'u aktif olur
     *
     * Kullanım: :pagination-options="paginationOptions"
     */
    paginationOptions() {
      return {
        enabled: true,
        mode: 'pages',
        jumpFirstOrLast: true,
        rowsPerPageLabel: this.t('Rows per page'),
        ofLabel: this.t('ofLabel'),
        nextLabel: this.t('nextLabel'),
        prevLabel: this.t('prevLabel'),
        pageLabel: this.t('page') || 'page',
        dropdownAllowAll: false
      };
    }
  },
  methods: {
    /**
     * VueGoodTable için pagination options (dinamik perPage ve currentPage ile)
     * @param {number} perPage - Sayfa başına kayıt sayısı
     * @param {number} currentPage - Mevcut sayfa numarası
     * @returns {object} Pagination options object
     *
     * Kullanım: :pagination-options="getPaginationOptions(filter.limit, filter.page)"
     */
    getPaginationOptions(perPage = 10, currentPage = 1, options = {}) {
      return {
        perPage,
        setCurrentPage: currentPage,
        enabled: true,
        mode: 'pages',
        jumpFirstOrLast: true,
        rowsPerPageLabel: this.t('Rows per page'),
        ofLabel: this.t('ofLabel'),
        nextLabel: this.t('nextLabel'),
        prevLabel: this.t('prevLabel'),
        pageLabel: this.t('page') || 'page',
        dropdownAllowAll: false,
        ...options
      };
    }
  }
};

export default vueGoodTablePagination;
